// What the builder's rows read besides the definition (UI-4): the engine's
// whitelist, the country's lists and segments for the membership pickers, and
// the value picker's suggestions — each fetched once per country, and the
// suggestions only for a field somebody actually opened.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  fetchAttributeKeys,
  fetchContactLists,
  fetchKnownValues,
  fetchSegmentFields,
  fetchSegments,
} from "@/lib/api";
import type { AttributeKeys, ContactListRef, KnownValues, Segment, SegmentField } from "@/lib/api";
import { catalogueOf } from "@/pages/segments/model";
import type { Catalogue } from "@/pages/segments/model";

export const COUNTRIES = ["RO", "PL", "HU", "GR", "SK", "EU", "BG", "IT", "CZ"];
// The segment list endpoint's own ceiling on `limit`.
export const SEGMENT_PAGE = 500;

export function messageOf(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return "Ismeretlen hiba.";
}

export function useCatalogue(): { catalogue: Catalogue | null; fields: SegmentField[]; error: string | null } {
  const [fields, setFields] = useState<SegmentField[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    fetchSegmentFields()
      .then((answer) => {
        if (!live) return;
        setFields(answer);
        setLoaded(true);
      })
      .catch((raised: unknown) => live && setError(messageOf(raised)));
    return () => {
      live = false;
    };
  }, []);
  const catalogue = useMemo(() => (loaded ? catalogueOf(fields) : null), [fields, loaded]);
  return { catalogue, fields, error };
}

export interface PickerOption {
  id: number;
  name: string;
  /** Why it cannot be picked, when it cannot. */
  disabled?: string;
}

export interface KnownValuesCache {
  get: (field: string) => KnownValues | "loading" | "failed" | undefined;
  load: (field: string) => void;
}

export interface BuilderEnv {
  country: string;
  lists: PickerOption[];
  segments: PickerOption[];
  attributes: AttributeKeys | null;
  values: KnownValuesCache;
}

/** A segment another may name: static, ready, this country, not itself
 * (`emc_db/segments/references.py` refuses the rest). */
function segmentOption(segment: Segment, selfId: number | null): PickerOption {
  let disabled: string | undefined;
  if (segment.kind !== "static") disabled = "dinamikus — csak statikus szegmensre lehet hivatkozni";
  else if (segment.state !== "ready") disabled = "nincs kész tagsága";
  if (segment.id === selfId) disabled = "saját magára nem hivatkozhat";
  return { id: segment.id, name: segment.name, disabled };
}

export function useBuilderEnv(country: string, selfId: number | null): BuilderEnv {
  const [lists, setLists] = useState<ContactListRef[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [attributes, setAttributes] = useState<AttributeKeys | null>(null);
  const [version, setVersion] = useState(0);
  const cache = useRef(new Map<string, KnownValues | "loading" | "failed">());

  useEffect(() => {
    let live = true;
    cache.current = new Map();
    setVersion((current) => current + 1);
    setLists([]);
    setSegments([]);
    setAttributes(null);
    fetchContactLists(country)
      .then((answer) => live && setLists(answer))
      .catch(() => live && setLists([]));
    fetchSegments(country, SEGMENT_PAGE)
      .then((answer) => live && setSegments(answer))
      .catch(() => live && setSegments([]));
    fetchAttributeKeys(country)
      .then((answer) => live && setAttributes(answer))
      .catch(() => live && setAttributes(null));
    return () => {
      live = false;
    };
  }, [country]);

  const load = useCallback(
    (field: string) => {
      if (cache.current.has(field)) return;
      cache.current.set(field, "loading");
      setVersion((current) => current + 1);
      const asked = cache.current;
      fetchKnownValues(country, field)
        .then((answer) => asked.set(field, answer))
        .catch(() => asked.set(field, "failed"))
        .finally(() => setVersion((current) => current + 1));
    },
    [country],
  );

  const values = useMemo<KnownValuesCache>(
    () => ({ get: (field) => cache.current.get(field), load }),
    // `version` is what tells the rows a suggestion list has arrived.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [load, version],
  );

  return {
    country,
    lists: useMemo(() => lists.map((item) => ({ id: item.id, name: item.name })), [lists]),
    segments: useMemo(
      () =>
        segments
          .filter((segment) => segment.state !== "archived")
          .map((segment) => segmentOption(segment, selfId)),
      [segments, selfId],
    ),
    attributes,
    values,
  };
}
